<script setup>
import axios from 'axios';
import { reactive, ref, watch } from 'vue';
import { accountTypeLabel } from '../format';
import { extractApiError } from '../apiError';
import { t } from '../i18n';

// Create / edit a composite portfolio: accounts, and per account all or some assets
// (docs/portfolios/SPEC.md, decisions 18, 21).
const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  portfolios: {
    type: Array,
    required: true,
  },
  // null = new group
  group: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'saved']);

const name = ref('');
const selectedAccounts = ref([]);
// portfolio id → chosen instrument uids
const assets = reactive({});
const expanded = reactive(new Set());
const saving = ref(false);
const error = ref('');

function assetUids(portfolio) {
  return [...new Set((portfolio.positions ?? []).map((p) => p.instrument_uid).filter(Boolean))];
}

function assetsOf(portfolio) {
  const seen = new Set();
  return (portfolio.positions ?? []).filter((p) => p.instrument_uid && !seen.has(p.instrument_uid) && seen.add(p.instrument_uid));
}

function reset() {
  error.value = '';
  expanded.clear();
  const members = new Map((props.group?.members ?? []).map((m) => [m.portfolio_id, m.instrument_uids]));
  name.value = props.group?.name ?? '';
  selectedAccounts.value = props.group ? [...members.keys()] : [];
  for (const portfolio of props.portfolios) {
    const all = assetUids(portfolio);
    const saved = members.get(portfolio.id);
    // A saved list keeps only assets still on the account; sold ones drop out on save.
    assets[portfolio.id] = Array.isArray(saved) ? all.filter((uid) => saved.includes(uid)) : all;
    if (Array.isArray(saved)) {
      expanded.add(portfolio.id);
    }
  }
}

function allAssetsChosen(portfolio) {
  return assets[portfolio.id]?.length === assetUids(portfolio).length;
}

function toggleAllAssets(portfolio) {
  assets[portfolio.id] = allAssetsChosen(portfolio) ? [] : assetUids(portfolio);
}

function toggleExpanded(id) {
  if (expanded.has(id)) {
    expanded.delete(id);
  } else {
    expanded.add(id);
  }
}

async function submit() {
  error.value = '';
  if (name.value.trim() === '') {
    error.value = t('group.needName');
    return;
  }
  const chosen = props.portfolios.filter((p) => selectedAccounts.value.includes(p.id));
  if (chosen.length === 0) {
    error.value = t('group.needAccount');
    return;
  }
  const empty = chosen.find((p) => (assets[p.id] ?? []).length === 0 && assetUids(p).length > 0);
  if (empty) {
    error.value = t('group.needAsset', { name: empty.name || empty.account_id });
    return;
  }

  const payload = {
    name: name.value.trim(),
    // All assets chosen → null, so assets bought later join automatically (decision 21).
    members: chosen.map((p) => ({
      portfolio_id: p.id,
      instrument_uids: allAssetsChosen(p) ? null : [...assets[p.id]],
    })),
  };

  saving.value = true;
  try {
    const { data } = props.group
      ? await axios.put(`/portfolio-groups/${props.group.id}`, payload)
      : await axios.post('/portfolio-groups', payload);
    emit('saved', data);
  } catch (e) {
    if (e?.response?.status !== 401) {
      error.value = extractApiError(e, t('group.saveFailed'));
    }
  } finally {
    saving.value = false;
  }
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      reset();
    }
  },
  { immediate: true }
);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[90] flex items-end justify-center bg-black/60 sm:items-center sm:p-4"
      @click.self="emit('close')"
    >
      <form
        class="flex max-h-[90vh] w-full flex-col sm:max-w-lg rounded-t-3xl bg-slate-900 p-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] shadow-2xl max-sm:max-h-[88dvh] max-sm:overflow-y-auto sm:rounded-xl sm:border sm:border-slate-700 sm:pb-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="portfolio-group-title"
        @submit.prevent="submit"
      >
        <h3
          id="portfolio-group-title"
          class="text-base font-semibold text-slate-100"
        >
          {{ group ? t('group.titleEdit') : t('group.titleNew') }}
        </h3>

        <label class="mt-4 block text-xs text-slate-400">
          {{ t('group.name') }}
          <input
            v-model="name"
            type="text"
            maxlength="100"
            class="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
          >
        </label>

        <p class="mt-4 text-xs text-slate-400">
          {{ t('group.accounts') }}
        </p>
        <ul class="mt-1 min-h-0 flex-1 space-y-1 overflow-y-auto">
          <li
            v-for="portfolio in portfolios"
            :key="portfolio.id"
            class="rounded border border-transparent"
            :class="selectedAccounts.includes(portfolio.id) ? 'border-slate-800 bg-slate-950/40' : ''"
          >
            <label class="flex cursor-pointer items-center gap-3 rounded px-2 py-1.5 hover:bg-slate-800/60">
              <input
                v-model="selectedAccounts"
                type="checkbox"
                class="accent-emerald-500"
                :value="portfolio.id"
              >
              <span class="min-w-0 flex-1">
                <span class="block truncate text-sm text-slate-200">{{ portfolio.name || portfolio.account_id }}</span>
                <span class="block text-xs text-slate-500">{{ accountTypeLabel(portfolio.type) }} · {{ portfolio.account_id }}</span>
              </span>
            </label>

            <div
              v-if="selectedAccounts.includes(portfolio.id) && assetUids(portfolio).length > 0"
              class="px-2 pb-2 pl-9"
            >
              <button
                type="button"
                class="text-xs text-emerald-400 hover:text-emerald-300"
                :aria-expanded="expanded.has(portfolio.id)"
                @click="toggleExpanded(portfolio.id)"
              >
                {{ allAssetsChosen(portfolio)
                  ? t('group.allAssets', { count: assetUids(portfolio).length })
                  : t('group.someAssets', { selected: assets[portfolio.id].length, count: assetUids(portfolio).length }) }}
                <span aria-hidden="true">{{ expanded.has(portfolio.id) ? '▴' : '▾' }}</span>
              </button>

              <div
                v-if="expanded.has(portfolio.id)"
                class="mt-1.5"
              >
                <label class="flex items-center gap-2 border-b border-slate-800 pb-1 text-xs text-slate-300">
                  <input
                    type="checkbox"
                    class="accent-emerald-500"
                    :checked="allAssetsChosen(portfolio)"
                    @change="toggleAllAssets(portfolio)"
                  >
                  {{ t('group.allAssetsToggle') }}
                </label>
                <p class="mt-1 text-[0.6875rem] text-slate-500">
                  {{ t('group.allAssetsHint') }}
                </p>
                <ul class="mt-1 space-y-0.5">
                  <li
                    v-for="position in assetsOf(portfolio)"
                    :key="position.instrument_uid"
                  >
                    <label class="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-slate-800/60">
                      <input
                        v-model="assets[portfolio.id]"
                        type="checkbox"
                        class="accent-emerald-500"
                        :value="position.instrument_uid"
                      >
                      <span class="min-w-0 flex-1 truncate text-slate-200">
                        {{ position.name || position.ticker || position.instrument_uid }}
                        <span
                          v-if="position.name && position.ticker"
                          class="text-slate-500"
                        >| {{ position.ticker }}</span>
                      </span>
                    </label>
                  </li>
                </ul>
              </div>
            </div>
          </li>
        </ul>

        <p
          v-if="error"
          class="mt-4 text-sm text-rose-400"
        >
          {{ error }}
        </p>

        <div class="mt-6 flex justify-end gap-2">
          <button
            type="button"
            class="rounded bg-slate-800 px-4 py-2 text-sm text-slate-200 hover:bg-slate-700"
            @click="emit('close')"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            type="submit"
            class="rounded bg-emerald-600 px-4 py-2 text-sm font-medium hover:bg-emerald-500 disabled:opacity-50"
            :disabled="saving"
          >
            {{ saving ? t('common.saving') : t('group.save') }}
          </button>
        </div>
      </form>
    </div>
  </Teleport>
</template>
